import { handleFetchMore } from '@sketch/components'
import {
  GetWorkspaceTransferDestinationsQuery,
  useGetWorkspaceTransferDestinationsQuery,
} from '@sketch/gql-types'
import { isTruthy } from '@sketch/utils'
import { useTreeState, useGetWorkspaces } from '../state'
import { dataIdFromObject } from '@sketch/graphql-cache'
import type { TreeSearch } from '../state.base'

export type TransferDestination =
  GetWorkspaceTransferDestinationsQuery['workspaceTransferDestinations']['entries'][number]

const ENTRIES_PATH = ['workspaceTransferDestinations', 'entries']

export interface UseSearchDestinationsProps {
  search: TreeSearch
}

export const useSearchDestinations = (props: UseSearchDestinationsProps) => {
  const { search } = props
  const treeState = useTreeState()

  const currentWorkspaceId = treeState.currentIds.workspaceId

  const workspacesQuery = useGetWorkspaces(currentWorkspaceId)

  const searchQuery = useGetWorkspaceTransferDestinationsQuery({
    variables: {
      workspaceIdentifier: currentWorkspaceId,
      search: { name: search.name },
    },
  })

  const loading = searchQuery.loading || workspacesQuery.loading
  const error = searchQuery.error || workspacesQuery.error

  const destinationsRaw =
    searchQuery.data?.workspaceTransferDestinations.entries || []

  const referencedWorkspaceIds = [
    ...new Set(
      destinationsRaw.map(x =>
        x.__typename === 'Workspace' ? x.identifier : x.workspace.identifier
      )
    ),
  ].filter(isTruthy)

  const allWorkspaces = workspacesQuery.workspaces?.allWorkspaces || []

  const workspaceDestinations = referencedWorkspaceIds
    .map(x => allWorkspaces.find(w => w.data.identifier === x)?.data)
    .filter(isTruthy)

  const projectDestinations = destinationsRaw.filter(
    x => x.__typename !== 'Workspace'
    // eslint-disable-next-line @typescript-eslint/ban-types
  ) as Extract<(typeof destinationsRaw)[number], { __typename: 'Project' }>[]

  const destinations = [...workspaceDestinations, ...projectDestinations]

  const totalCount =
    searchQuery.data?.workspaceTransferDestinations.meta.totalCount
  const after = searchQuery.data?.workspaceTransferDestinations.meta.after
  const fetchMore = searchQuery.fetchMore

  const loadMore = handleFetchMore(fetchMore, ENTRIES_PATH, {
    dataIdFromObject,
    after,
  })

  return {
    loading,
    error,
    destinations,
    workspaceDestinations,
    projectDestinations,
    after,
    loadMore,
    totalCount,
  }
}
